import { z } from "zod";
import { aiEstimateCostFields, refineAiEstimateCost } from "./ai-model-policy.js";
import { ufSchema } from "./calculation.js";
import { usdAmountSchema } from "./web-research.js";
/*
 * Autonomous research scheduler ("Pesquisa autônoma").
 *
 * A daily Cloud Run job collects open coverage gaps and the national base
 * data still missing, ranks them and queues research through the same paths
 * an operator uses: calculator research requests ("Pesquisar lacunas"), web
 * research sweeps ("Nova pesquisa") and agent-runner questions ("Pesquisar
 * uma pergunta"). It never approves, verifies or publishes anything: results
 * land as UNVERIFIED facts, NEW documents or tasks waiting for review.
 *
 * Every run is bounded by a daily estimated budget (US$), a max number of
 * items, a kill switch and a per-target backoff after repeated inconclusive
 * attempts. The admin API reads the runs for the Agentes → Pesquisa web page.
 */
export const AUTONOMOUS_RESEARCH_DEFAULTS = {
    /** Estimated spend the scheduler may queue per São Paulo calendar day (US$). */
    dailyBudgetUsd: "5",
    /** Items (gaps, sweeps, questions) one run may queue. */
    maxItemsPerRun: 70,
    /** Consecutive inconclusive attempts before a target backs off. */
    backoffAfterInconclusive: 3,
    /** Days a target waits once it backs off. */
    backoffDays: 14,
    /** Daily cron (minute hour * * *) in `timeZone`. */
    schedule: "30 7 * * *",
    timeZone: "America/Sao_Paulo",
};
/** Items one run record keeps (queued first, then the best skipped ones). */
export const AUTONOMOUS_RESEARCH_MAX_STORED_ITEMS = 200;
/** Runs the overview lists. */
export const AUTONOMOUS_RESEARCH_RECENT_RUNS = 14;
/**
 * LOT_GAP: an open coverage gap seen in real calculations/lots (weighted by
 * rows and recency). PRIORITY_ROUTE: a gap on one of the owner's priority
 * routes (Mondelez and the test package). NATIONAL_BASE: base data still
 * missing nationally (segment-17 MVA by UF, an internal rate, open questions).
 */
export const autonomousResearchTierSchema = z.enum(["PRIORITY_ROUTE", "LOT_GAP", "NATIONAL_BASE"]);
/** The operator path the item was queued through. */
export const autonomousResearchPathSchema = z.enum([
    "CALCULATOR_RESEARCH",
    "WEB_RESEARCH_SWEEP",
    "AGENT_QUESTION",
]);
/**
 * QUEUED: new research was queued (counts towards the budget).
 * REUSED: a finished result is still inside the reuse window (nothing queued).
 * JOINED_ACTIVE: research for the same target is already running.
 * SKIPPED_*: not queued this run, and why.
 * FAILED: queueing raised an error (the run goes on with the next item).
 */
export const autonomousResearchItemOutcomeSchema = z.enum([
    "QUEUED",
    "REUSED",
    "JOINED_ACTIVE",
    "SKIPPED_BUDGET",
    "SKIPPED_MAX_ITEMS",
    "SKIPPED_BACKOFF",
    "SKIPPED_UNAVAILABLE",
    "FAILED",
]);
/** How the previous attempt for the same target ended (null: first attempt). */
export const autonomousResearchAttemptOutcomeSchema = z.enum([
    "PENDING",
    "CONCLUSIVE",
    "INCONCLUSIVE",
]);
export const autonomousResearchItemSchema = z
    .object({
    /** Stable key of the target (same gap or base item across runs). */
    targetKey: z.string().min(1).max(200),
    tier: autonomousResearchTierSchema,
    path: autonomousResearchPathSchema,
    /** Short human label, e.g. "1806.32.20 ES→SP" or "MVA seg. 17 · AL". */
    label: z.string().min(1).max(200),
    originUf: ufSchema.nullable(),
    destinationUf: ufSchema.nullable(),
    ncm: z
        .string()
        .regex(/^\d{8}$/)
        .nullable(),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .nullable(),
    /** Ranking score (higher first). */
    score: z.number().nonnegative(),
    /** Calculation rows behind the gap (0 for base data). */
    rows: z.number().int().nonnegative(),
    estimatedCostUsd: aiEstimateCostFields.costUsd,
    costUnavailableReason: aiEstimateCostFields.costUnavailableReason,
    outcome: autonomousResearchItemOutcomeSchema,
    /** Re-researched inside the reuse window because the last result was empty or inconclusive. */
    forced: z.boolean(),
    previousOutcome: autonomousResearchAttemptOutcomeSchema.nullable(),
    /** Consecutive inconclusive attempts before this run. */
    inconclusiveAttempts: z.number().int().nonnegative(),
    /** Backoff end when outcome is SKIPPED_BACKOFF. */
    backoffUntil: z.string().datetime({ offset: true }).nullable(),
    requestId: z.string().uuid().nullable(),
    sweepId: z.string().uuid().nullable(),
    taskId: z.string().uuid().nullable(),
    /** Error class or skip detail, never a secret. */
    detail: z.string().max(300).nullable(),
})
    .strict()
    .superRefine((value, ctx) => refineAiEstimateCost({ ...value, costUsd: value.estimatedCostUsd }, ctx));
export const autonomousResearchRunStatusSchema = z.enum([
    "RUNNING",
    "COMPLETED",
    "DISABLED",
    "FAILED",
]);
export const autonomousResearchCountsSchema = z
    .object({
    candidates: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    reused: z.number().int().nonnegative(),
    joined: z.number().int().nonnegative(),
    skippedBudget: z.number().int().nonnegative(),
    skippedMaxItems: z.number().int().nonnegative(),
    skippedBackoff: z.number().int().nonnegative(),
    skippedUnavailable: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** Earlier attempts settled at the start of this run. */
    settledConclusive: z.number().int().nonnegative(),
    settledInconclusive: z.number().int().nonnegative(),
    byTier: z
        .object({
        PRIORITY_ROUTE: z.number().int().nonnegative(),
        LOT_GAP: z.number().int().nonnegative(),
        NATIONAL_BASE: z.number().int().nonnegative(),
    })
        .strict(),
    byPath: z
        .object({
        CALCULATOR_RESEARCH: z.number().int().nonnegative(),
        WEB_RESEARCH_SWEEP: z.number().int().nonnegative(),
        AGENT_QUESTION: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
export const autonomousResearchLimitsSchema = z
    .object({
    dailyBudgetUsd: usdAmountSchema,
    maxItemsPerRun: z.number().int().min(1).max(AUTONOMOUS_RESEARCH_MAX_STORED_ITEMS),
    backoffAfterInconclusive: z.number().int().min(1).max(20),
    backoffDays: z.number().int().min(1).max(180),
    reuseDays: z.number().int().min(1).max(365),
})
    .strict();
export const autonomousResearchRunSummarySchema = z
    .object({
    id: z.string().uuid(),
    status: autonomousResearchRunStatusSchema,
    startedAt: z.string().datetime({ offset: true }),
    finishedAt: z.string().datetime({ offset: true }).nullable(),
    /** Cloud Run execution name, when known. */
    executionName: z.string().max(200).nullable(),
    /** Estimated spend this run queued (US$). */
    estimatedSpendUsd: usdAmountSchema,
    /** Estimated spend queued earlier the same São Paulo day (US$). */
    spentTodayBeforeUsd: usdAmountSchema,
    counts: autonomousResearchCountsSchema,
    error: z.string().max(300).nullable(),
})
    .strict();
export const autonomousResearchRunSchema = autonomousResearchRunSummarySchema
    .extend({
    limits: autonomousResearchLimitsSchema,
    items: z.array(autonomousResearchItemSchema).max(AUTONOMOUS_RESEARCH_MAX_STORED_ITEMS),
})
    .strict();
/** `GET /v1/autonomous-research` (ADMIN:TAX_PLATFORM:ALL). */
export const autonomousResearchOverviewSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** Kill switch as deployed (false: the job starts, records DISABLED and queues nothing). */
    enabled: z.boolean(),
    schedule: z
        .object({
        cron: z.string().min(1).max(60),
        timeZone: z.string().min(1).max(60),
        nextRunAt: z.string().datetime({ offset: true }).nullable(),
    })
        .strict(),
    limits: autonomousResearchLimitsSchema,
    /** Estimated spend queued so far today (São Paulo day). */
    spentTodayUsd: usdAmountSchema,
    latestRun: autonomousResearchRunSchema.nullable(),
    recentRuns: z.array(autonomousResearchRunSummarySchema).max(AUTONOMOUS_RESEARCH_RECENT_RUNS),
})
    .strict();
//# sourceMappingURL=autonomous-research.js.map